import React, { useEffect, useState } from 'react';

import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';

import styles from './Visualiser.module.css';
import 'react-tabs/style/react-tabs.css';

import { usePersonalisationContext } from '../../context/PersonalisationContext';
import { PersonalisationLoader } from '../PersonalisationLoader';

const VIS_ENABLED = window.personalisation.visEnabled;

/**
 * Types for the Visualiser configuration
 */
export type VisualiserSlot = {
  id: string;
  label: string;
  paramKey: string;
  options: { value: string; label: string }[];
  selected?: string;
  visualised?: boolean;
};

export type VisualiserConfig = {
  layoutId: string;
  imageBase: string;
  slots: VisualiserSlot[];
  productVariantId: string;
};

/**
 * Visualiser component
 *
 * Receives config and selected slot values, and renders the image.
 * All controls and state are handled by the wrapper.
 *
 * @param {Object} props
 * @param {VisualiserConfig} props.config - The configuration for the visualiser
 */
export const Visualiser = ({ width = 400 }: { width: number }) => {
  const { visualiserImageUrl, visualisationGroups, lastModifiedGroupId } = usePersonalisationContext();
  const [imageUrls, setImageUrls] = useState<
    { url: string; visualiserSlotMode: string; groupId: string; label: string; order: number }[]
  >([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [loadingUrls, setLoadingUrls] = useState(false);
  const [loadingImages, setLoadingImages] = useState<
    Map<string, { previousUrl: string | null; currentUrl: string | null }>
  >(new Map());

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoadingUrls(true);

      const visItems: { url: string; visualiserSlotMode: string; groupId: string; order: number }[] =
        await visualiserImageUrl;

      // Ignore stale results from previous renders
      if (cancelled) return;

      // Clear imageUrls if visualisation is disabled or no items
      if (VIS_ENABLED !== 'true' || !visItems || visItems?.length === 0) {
        setImageUrls([]);
        setLoadingUrls(false);
        setLoadingImages(new Map());
        return;
      }

      const processedUrls = visItems
        .map((item) => {
          // Get the group data from the visualisationGroups Map
          const groupData = visualisationGroups.get(item.groupId);

          return {
            url: item.url.startsWith('data:') ? item.url : item.url + `&w=${width}`,
            visualiserSlotMode: item.visualiserSlotMode,
            groupId: item.groupId,
            label: groupData?.label || `Group ${item.groupId}`, // fallback label
            order: item.order
          };
        })
        // Ensure the order is retained from the visualisation groups map.
        .sort((a, b) => a.order - b.order);

      // Compare new URLs with existing ones to determine which images need loading.
      setImageUrls((prevImageUrls) => {
        const urlMap = new Map(prevImageUrls.map((item) => [item.groupId, item.url]));

        // Update loading state for images that have changed URLs.
        setLoadingImages((prevLoading) => {
          const nextLoading = new Map(prevLoading);

          // Remove groupIds that are no longer in the new URLs.
          prevImageUrls.forEach((oldItem) => {
            if (!processedUrls.some((newItem) => newItem.groupId === oldItem.groupId)) {
              nextLoading.delete(oldItem.groupId);
            }
          });

          // Update or add loading state for images with changed URLs.
          processedUrls.forEach((newItem) => {
            const previousUrl = urlMap.get(newItem.groupId) || null;
            const currentUrl = newItem.url;

            // Only track if URL has changed or is new (previousUrl is null)
            if (previousUrl !== currentUrl) {
              nextLoading.set(newItem.groupId, {
                previousUrl,
                currentUrl
              });
            } else {
              // URL hasn't changed, remove from loading state
              nextLoading.delete(newItem.groupId);
            }
          });

          return nextLoading;
        });

        return processedUrls;
      });

      setLoadingUrls(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [visualiserImageUrl, visualisationGroups, width]);

  const handleImageLoad = (groupId: string) => {
    setLoadingImages((prev) => {
      const next = new Map(prev);
      next.delete(groupId);
      return next;
    });
  };

  const handleImageError = (groupId: string) => {
    setLoadingImages((prev) => {
      const next = new Map(prev);
      next.delete(groupId);
      return next;
    });
  };

  // Auto-switch to the tab that corresponds to the last modified group
  useEffect(() => {
    if (lastModifiedGroupId && imageUrls.length > 1) {
      const tabIndex = imageUrls.findIndex((imageUrl) => imageUrl.groupId === lastModifiedGroupId);
      if (tabIndex !== -1) {
        setActiveTabIndex(tabIndex);
      }
    }
  }, [lastModifiedGroupId, imageUrls]);

  // If the visualiser is not enabled, don't render the component.
  if (VIS_ENABLED !== 'true') return null;

  const activeImageUrl = imageUrls[activeTabIndex];

  // Helper function to check if we should show loader for a specific group
  const shouldShowLoader = (groupId: string) => {
    const loadingState = loadingImages.get(groupId);
    if (!loadingState) return false;
    // Show loader only when transitioning from null/empty to a value
    return loadingState.previousUrl === null && loadingState.currentUrl !== null;
  };

  // --- Render only the image ---
  if (imageUrls.length === 1) {
    const singleImageUrl = imageUrls[0];
    const isSingleImageLoading = loadingImages.has(singleImageUrl.groupId);
    const showLoader = shouldShowLoader(singleImageUrl.groupId);

    return (
      <div className={styles['visualiser']}>
        <div className={styles['visualiser__image-wrapper']}>
          {showLoader && (
            <div className={styles['visualiser__loader-wrapper']}>
              <PersonalisationLoader isVisible={true} position="absolute" />
            </div>
          )}
          <img
            src={singleImageUrl.url}
            alt="Visualisation Preview"
            className={styles['visualiser__image']}
            onLoad={() => handleImageLoad(singleImageUrl.groupId)}
            onError={() => handleImageError(singleImageUrl.groupId)}
            style={{ opacity: isSingleImageLoading ? 0.5 : 1 }}
          />
        </div>
      </div>
    );
  }

  if (imageUrls.length > 1) {
    return (
      <div className={styles['visualiser']}>
        <Tabs className={styles['visualiser__tabs']} selectedIndex={activeTabIndex} onSelect={setActiveTabIndex}>
          {imageUrls.map((imageUrl) => {
            const isImageLoading = loadingImages.has(imageUrl.groupId);
            const isActive = imageUrl.groupId === activeImageUrl?.groupId;
            const showLoader = shouldShowLoader(imageUrl.groupId);

            return (
              <TabPanel
                key={imageUrl.groupId + 'tabPanel'}
                selectedClassName={styles['visualiser__tabs-panel--selected']}
                className={styles['visualiser__tabs-panel']}
              >
                <div className={styles['visualiser__image-wrapper']}>
                  {isActive && showLoader && (
                    <div className={styles['visualiser__loader-wrapper']}>
                      <PersonalisationLoader isVisible={true} position="absolute" />
                    </div>
                  )}
                  <img
                    key={imageUrl.groupId + 'image'}
                    src={imageUrl.url}
                    alt="Visualisation Preview"
                    className={styles['visualiser__image']}
                    onLoad={() => handleImageLoad(imageUrl.groupId)}
                    onError={() => handleImageError(imageUrl.groupId)}
                    style={{ opacity: isImageLoading && isActive ? 0.5 : 1 }}
                  />
                </div>
              </TabPanel>
            );
          })}
          <TabList className={styles['visualiser__tabs-list']}>
            {imageUrls.map((imageUrl) => (
              <Tab
                key={imageUrl.groupId + 'tabList'}
                selectedClassName={styles['visualiser__tabs-list-item--selected']}
                className={styles['visualiser__tabs-list-item'] + ' body-sm'}
              >
                {imageUrl.label}
              </Tab>
            ))}
          </TabList>
        </Tabs>
      </div>
    );
  }

  return null;
};
